import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import CommonBinder from '../common-binder';
import { Gizmo } from '../../../src/gizmos';
import {
  IBinderSetDataParams,
  ICodeScanner,
  IGizmo,
  IGizmoData,
  IGizmoEvent,
} from '../../types';

interface ITextFieldBinder {
  init(): Promise<void>;
  setValue(value: string): Promise<void>;
  getValue(): string | undefined;
  value: Subject<string>;
}

class TextFieldBinder extends CommonBinder implements ITextFieldBinder {
  /**
   * Variable to control de code scanner in the UI
   */
  private _codeScanner?: ICodeScanner;
  /**
   * Observable which will be used by the UI layer in order to avoid Flowy
   * data format
   */
  public value: Subject<string>;
  /**
   * Observable used by the core to control the code scanner
   */
  public codeScanner: Subject<ICodeScanner>;

  private setValue$ = new Subject<string>();

  constructor(gizmo: Gizmo) {
    super(gizmo);

    this.value = new Subject<string>();
    this.codeScanner = new Subject<ICodeScanner>();

    this._gizmo.data.subscribe(
      (data: IGizmoData | IGizmoData[] | undefined) => {
        if (data && !Array.isArray(data) && typeof data.v === 'string') {
          this.value.next(data.v);
        } else if (data === undefined) {
          this.value.next('');
        }
      }
    );

    this.setValue$.pipe(debounceTime(500)).subscribe(async value => {
      await this.setValue(value);
    });
  }

  public init = (): Promise<void> => {
    return new Promise<void>(async resolve => {
      const config: IGizmo = this._gizmo.getConfig();

      if (
        config.ops?.textField?.codeScannerEnabled ||
        config.ops?.textField?.codeScanner?.enabled
      ) {
        this._codeScanner = {
          show: false,
          // label: config.ops.textField.codeScannerLabel || 'Leer QR',
          label: 'Leer QR',
        };
      }

      // If the gizmo has no data and we have value configuration we proceed to evalute the Flux
      // script
      if (config.ops?.value?.flux && !this._gizmo.getData()) {
        const flux = this._gizmo.getFlux();

        if (flux) {
          let conditionResult = true;

          if (config.ops.value.condition) {
            const cr = await flux.evaluate({
              script: config.ops.value.condition,
            });

            if (cr !== undefined) {
              conditionResult = cr;
            }
          }

          if (conditionResult === true) {
            const result = await flux.evaluate({
              script: config.ops.value.flux,
            });

            if (result && typeof result === 'string') {
              await this.setValue(result);
            }
          }
        }
      }
      resolve();
    });
  };

  public setValueWithDebounce = (value: string): void => {
    this.value.next(value);
    this.setValue$.next(value);
  };

  /**
   * Function used to set an answer
   * @param value value being set by the UI layer
   */
  public setValue = (value?: string): Promise<void> => {
    return new Promise<void>(async resolve => {
      const config = this._gizmo.getConfig();

      if (value !== undefined) {
        if (config.ops?.textField?.toUppercase && typeof value === 'string') {
          value = value.toUpperCase().replace(/[^A-Z0-9ñ\sáéíóúü\.]+/gi, '');
        }

        await this._gizmo.setData({ v: value });
      } else {
        await this._gizmo.setData(undefined);
      }

      resolve();
    });
  };

  /**
   * Set data to the gizmo
   * @param data gizmo data to be set
   */
  public setData = async ({ data }: IBinderSetDataParams) => {
    if (data.v && typeof data.v === 'string') {
      await this._gizmo.setData(data);
    }
  };

  /**
   * Set the value of the text field when it comes from a code scanner
   *
   * @param {string} result reading from the code scanner
   * @returns
   */
  public setCodeScannerResult = (result: string): Promise<void> => {
    return new Promise<void>(async resolve => {
      const config = this._gizmo.getConfig();
      let gizmoData: IGizmoData = { v: result };

      if (config.ops?.textField?.codeScanner?.processCurp) {
        const parts = result.split('|');
        const pipeCount = parts.length - 1;

        if (pipeCount === 9) {
          const curpConfig: any = config.ops.textField.codeScanner.curp;
          // If we have 9 pipes we're going to assume that the result is a CURP
          gizmoData = {
            v: parts[0],
            td: {
              scannedCurp: result,
            },
          };
          // TODO: should we remove this assignment?
          result = parts[0];

          for (const key in curpConfig) {
            const prefix = key.replace(/(GizmoFid|GizmoUid)$/, '');
            const gizmoFid = curpConfig[key];

            let dataIndex = -1;
            if (prefix === 'curp') {
              dataIndex = 0;
            } else if (prefix === 'name') {
              dataIndex = 4;
            } else if (prefix === 'lastName') {
              dataIndex = 2;
            } else if (prefix === 'secondLastName') {
              dataIndex = 3;
            } else if (prefix === 'sex') {
              dataIndex = 5;
            } else if (prefix === 'birthDate') {
              dataIndex = 6;
            } else if (prefix === 'entity') {
              dataIndex = 7;
            } else if (prefix === 'entityCode') {
              dataIndex = 8;
            }

            if (dataIndex > -1) {
              const value = parts[dataIndex];

              if (value) {
                const event: IGizmoEvent = {
                  type: 'gizmo.set-data',
                  src: {
                    type: 'gizmo',
                    gizmoFid: config.fid,
                  },
                  dest: {
                    type: 'gizmo',
                    gizmoFid,
                  },
                  data: {
                    gizmoSetData: {
                      value,
                    },
                  },
                };

                this._gizmo.getEvents().next(event);
              }
            }
          }
        }
      }

      await this._gizmo.setData(gizmoData);

      // After setting the value we're going to hide the code scanner
      if (this._codeScanner) {
        this._codeScanner.show = false;
        this.codeScanner.next(this._codeScanner);
      }

      resolve();
    });
  };

  /**
   * Get text value directly as a string
   *
   * @returns {string | undefined} text value
   */
  public getValue = (): string | undefined => {
    const data = this._gizmo.getData() as IGizmoData;

    if (data?.v && typeof data.v === 'string') {
      return data.v;
    }

    return undefined;
  };

  /**
   * Send a special key to the core
   *
   * @param {string} key special key being sent
   * @returns
   */
  public sendSpecialKey = (key: string): Promise<void> => {
    return new Promise<void>(async resolve => {
      if (key === 'enter') {
        // Right now we're only supporting enter key
        await this._gizmo.dispatchGizmoOperation({
          operation: 'keyboard.key',
          params: {
            key,
          },
        });
      }

      resolve();
    });
  };

  /**
   * Code scanner getter
   * @returns {ICodeScanner | undefined} code scanner configuration
   */
  public getCodeScanner = (): ICodeScanner | undefined => {
    return this._codeScanner;
  };

  /**
   * Toggle the code scanner show flag
   */
  public toggleCodeScanner = () => {
    if (this._codeScanner) {
      this._codeScanner.show = !this._codeScanner.show;
      this.codeScanner.next(this._codeScanner);
    }
  };
}

export default TextFieldBinder;
